import type { CompanyKpiRepository } from "../../../domain/repositories/company/company-kpi-repository";
import {
  type CompanyKpiMachine,
  CompanyKpiMachineListSchema,
  type CompanyKpiSlot,
  CompanyKpiSlotListSchema,
  CompanyKpiSlotSchema,
  type CompanyKpiSlotStat,
  CompanyKpiSlotStatListSchema,
  type ComputeRequest,
  type ComputeResponse,
  ComputeResponseSchema,
  type CreateKpiSlotRequest,
  type KpiStatisticsResponse,
  KpiStatisticsResponseSchema,
  type MesDashboardCard,
  MesDashboardCardListSchema,
  MesDashboardCardSchema,
  type ProductionLogResponse,
  ProductionLogResponseSchema,
  type SaveCardRequest,
  type UpdateKpiSlotRequest,
} from "../../../domain/types/company/company-kpi";
import { companyKpiApiService } from "../../api/company/company-kpi-api-service";

export class CompanyKpiRepositoryImpl implements CompanyKpiRepository {
  async getStatistics(): Promise<KpiStatisticsResponse> {
    const response = await companyKpiApiService.getStatistics();

    if (response.data) {
      return KpiStatisticsResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "KPI 통계 가져오기 실패.");
  }

  async getStatisticsPeriod(
    startDate: string,
    endDate: string,
  ): Promise<KpiStatisticsResponse> {
    const response = await companyKpiApiService.getStatisticsPeriod(
      startDate,
      endDate,
    );

    if (response.data) {
      return KpiStatisticsResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "KPI 통계 가져오기 실패.");
  }

  async getProductionLogs(
    date: string,
    machineIds?: number[],
  ): Promise<ProductionLogResponse> {
    const response = await companyKpiApiService.getProductionLogs(
      date,
      machineIds,
    );

    if (response.data) {
      return ProductionLogResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "생산량 로그 가져오기 실패.");
  }

  async getSlots(): Promise<CompanyKpiSlot[]> {
    const response = await companyKpiApiService.getSlots();

    if (response.data) {
      return CompanyKpiSlotListSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "KPI 항목 가져오기 실패.");
  }

  async updateSlot(
    id: number,
    body: UpdateKpiSlotRequest,
  ): Promise<CompanyKpiSlot> {
    const response = await companyKpiApiService.updateSlot(id, body);

    if (response.data) {
      return CompanyKpiSlotSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "KPI 항목 수정 실패.");
  }

  async createSlot(body: CreateKpiSlotRequest): Promise<CompanyKpiSlot> {
    const response = await companyKpiApiService.createSlot(body);

    if (response.data) {
      return CompanyKpiSlotSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "KPI 항목 추가 실패.");
  }

  async deleteSlot(id: number): Promise<void> {
    await companyKpiApiService.deleteSlot(id);
  }

  async getSlotStatistics(
    startDate: string,
    endDate: string,
    machineId?: number,
  ): Promise<CompanyKpiSlotStat[]> {
    const response = await companyKpiApiService.getSlotStatistics(
      startDate,
      endDate,
      machineId,
    );

    if (response.data) {
      return CompanyKpiSlotStatListSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "KPI 통계 가져오기 실패.");
  }

  async getMachines(): Promise<CompanyKpiMachine[]> {
    const response = await companyKpiApiService.getMachines();

    if (response.data) {
      return CompanyKpiMachineListSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "장비 목록 가져오기 실패.");
  }

  // ─── KPI 2.6 카드 대시보드 ──────────────────────────────────────────────────

  async compute(body: ComputeRequest): Promise<ComputeResponse> {
    const response = await companyKpiApiService.compute(body);

    if (response.data) {
      return ComputeResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "동적 계산 실패.");
  }

  async getCards(): Promise<MesDashboardCard[]> {
    const response = await companyKpiApiService.getCards();

    if (response.data) {
      return MesDashboardCardListSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "카드 목록 가져오기 실패.");
  }

  async createCard(body: SaveCardRequest): Promise<MesDashboardCard> {
    const response = await companyKpiApiService.createCard(body);

    if (response.data) {
      return MesDashboardCardSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "카드 추가 실패.");
  }

  async updateCard(id: number, body: SaveCardRequest): Promise<MesDashboardCard> {
    const response = await companyKpiApiService.updateCard(id, body);

    if (response.data) {
      return MesDashboardCardSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "카드 수정 실패.");
  }

  async deleteCard(id: number): Promise<void> {
    await companyKpiApiService.deleteCard(id);
  }

  async reorderCards(cardIds: number[]): Promise<MesDashboardCard[]> {
    const response = await companyKpiApiService.reorderCards(cardIds);

    if (response.data) {
      return MesDashboardCardListSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "카드 순서 변경 실패.");
  }
}
